import { useState } from "react";
import { useParams } from "@tanstack/react-router";
import { Activity, AlertTriangle, Building2, Plus, StickyNote, Trash2 } from "lucide-react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DataList,
  EmptyState,
  KeyValue,
  LoadingBlock,
  PageHeader,
  SelectField,
  Sheet,
  StatusBadge,
  TextAreaField,
  TextField,
  type DataColumn,
} from "@nahal/ui";
import type { Workspace } from "@nahal/api-client";
import { ModuleGrantsSheet } from "./ModuleGrantsSheet";
import { entitlementPayload, entitlementRows, reservedEntitlement, type EntitlementRow } from "./entitlementEditor";
import {
  useAddNote,
  useChangeWorkspaceStatus,
  useCreateCompany,
  useCreateMarketplace,
  useCreateWorkspace,
  useHealth,
  useIncidents,
  useNotes,
  useOrganization,
  usePlans,
  useProducts,
  useSetEntitlements,
  useModuleFeature,
} from "../../lib/queries";

type Tab = "overview" | "health" | "incidents" | "notes";

const tabs: Array<{ key: Tab; label: string; icon: typeof Building2 }> = [
  { key: "overview", label: "Overview", icon: Building2 },
  { key: "health", label: "Health", icon: Activity },
  { key: "incidents", label: "Incidents", icon: AlertTriangle },
  { key: "notes", label: "Notes", icon: StickyNote },
];

export function CustomerScreen() {
  const { organizationId } = useParams({ from: "/customers/$organizationId" });
  const [tab, setTab] = useState<Tab>("overview");
  const organization = useOrganization(organizationId);

  if (organization.isLoading) return <LoadingBlock />;
  if (!organization.data) return <EmptyState title="Customer not found" />;

  const { organization: org, members, subscriptions, workspaces } = organization.data;

  return (
    <>
      <PageHeader
        title={org.name}
        description={org.billingEmail ?? undefined}
        meta={
          <>
            <StatusBadge status={org.status} />
            <span className="text-xs text-[var(--text-muted)]">
              since {new Date(org.createdAt).toLocaleDateString()}
            </span>
          </>
        }
      />

      {/* Scrolls horizontally on a phone rather than wrapping into two rows. */}
      <div className="-mx-3 mb-3 overflow-x-auto px-3 md:mx-0 md:px-0">
        <div className="inline-flex gap-1 rounded-[var(--radius-control)] bg-[var(--surface-sunken)] p-1">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={
                "inline-flex min-h-9 items-center gap-1.5 whitespace-nowrap rounded-[var(--radius-control)] px-3 text-sm font-medium transition-colors " +
                (tab === item.key
                  ? "bg-[var(--surface-card)] text-[var(--text-primary)] shadow-[var(--shadow-raised)]"
                  : "text-[var(--text-secondary)]")
              }
            >
              <item.icon className="size-4" />
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <OverviewTab
          organizationId={organizationId}
          workspaces={workspaces}
          members={members.length}
          subscriptions={subscriptions}
        />
      )}
      {tab === "health" && <HealthTab organizationId={organizationId} />}
      {tab === "incidents" && <IncidentsTab organizationId={organizationId} />}
      {tab === "notes" && <NotesTab organizationId={organizationId} />}
    </>
  );
}

function OverviewTab({
  organizationId,
  workspaces,
  members,
  subscriptions,
}: {
  organizationId: string;
  workspaces: Workspace[];
  members: number;
  subscriptions: Array<{ id: string; productCode: string; planName: string | null; status: string }>;
}) {
  const change = useChangeWorkspaceStatus(organizationId);
  const moduleAccessEnabled = useModuleFeature();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Workspace | null>(null);
  const [moduleWorkspace, setModuleWorkspace] = useState<Workspace | null>(null);
  const [suspending, setSuspending] = useState<Workspace | null>(null);
  const [provisioning, setProvisioning] = useState<{
    workspace: Workspace;
    kind: "company" | "marketplace";
  } | null>(null);

  const columns: Array<DataColumn<Workspace>> = [
    { key: "name", header: "Workspace", primary: true, cell: (row) => row.name },
    { key: "product", header: "Product", cell: (row) => row.productCode },
    { key: "status", header: "Status", cell: (row) => <StatusBadge status={row.status} /> },
    { key: "host", header: "Host", desktopOnly: true, cell: (row) => row.primaryHost ?? "-" },
    { key: "ref", header: "Ref", desktopOnly: true, cell: (row) => row.externalRef ?? "-" },
  ];

  return (
    <div className="space-y-4">
      <Card>
        <CardBody>
          <KeyValue
            items={[
              { label: "Members", value: members },
              { label: "Workspaces", value: workspaces.length },
              {
                label: "Subscriptions",
                value:
                  subscriptions.length === 0
                    ? "none"
                    : subscriptions
                        .map((s) => `${s.productCode}${s.planName ? ` (${s.planName})` : ""}`)
                        .join(", "),
              },
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Workspaces"
          action={
            <Button
              size="sm"
              variant="solid"
              icon={<Plus className="size-4" />}
              onClick={() => setCreating(true)}
            >
              New
            </Button>
          }
        />
        <DataList
          columns={columns}
          rows={workspaces}
          rowKey={(row) => row.id}
          empty={{ title: "No workspaces", description: "Provision one with New." }}
          rowActions={(row) => (
            <>
              {moduleAccessEnabled && <Button size="sm" variant="ghost" onClick={() => setModuleWorkspace(row)}>
                Modules
              </Button>}
              <Button size="sm" variant="ghost" onClick={() => setEditing(row)}>
                {moduleAccessEnabled ? "Quotas & other entitlements" : "Entitlements"}
              </Button>
              {row.status === "ACTIVE" && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSuspending(row)}
                >
                  Suspend
                </Button>
              )}
              {row.status === "SUSPENDED" && (
                <Button
                  size="sm"
                  variant="soft"
                  onClick={() => change.mutate({ workspaceId: row.id, status: "ACTIVE" })}
                >
                  Reactivate
                </Button>
              )}
              {row.status !== "ARCHIVED" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => change.mutate({ workspaceId: row.id, status: "ARCHIVED" })}
                >
                  Archive
                </Button>
              )}
              {row.status === "ACTIVE" && row.externalRef && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => setProvisioning({ workspace: row, kind: "company" })}>
                    Add company
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setProvisioning({ workspace: row, kind: "marketplace" })}
                  >
                    Add marketplace
                  </Button>
                </>
              )}
              {row.provisioningError && (
                <span className="text-xs text-danger-600">{row.provisioningError}</span>
              )}
            </>
          )}
        />
      </Card>

      {moduleAccessEnabled && moduleWorkspace && (
        <ModuleGrantsSheet organizationId={organizationId} workspace={moduleWorkspace} onClose={() => setModuleWorkspace(null)} />
      )}
      <CreateWorkspaceSheet
        organizationId={organizationId}
        open={creating}
        onClose={() => setCreating(false)}
      />
      <EntitlementsSheet
        organizationId={organizationId}
        workspace={editing}
        onClose={() => setEditing(null)}
      />
      <SuspendWorkspaceSheet
        workspace={suspending}
        pending={change.isPending}
        onClose={() => setSuspending(null)}
        onConfirm={() => {
          if (!suspending) return;
          change.mutate(
            { workspaceId: suspending.id, status: "SUSPENDED" },
            { onSuccess: () => setSuspending(null) },
          );
        }}
      />
      <ProvisionSheet
        organizationId={organizationId}
        target={provisioning}
        onClose={() => setProvisioning(null)}
      />
    </div>
  );
}

function SuspendWorkspaceSheet({
  workspace,
  pending,
  onClose,
  onConfirm,
}: {
  workspace: Workspace | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Sheet
      open={workspace !== null}
      title="Suspend workspace?"
      description="This is an immediate, disruptive action."
      onClose={pending ? () => {} : onClose}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="danger" size="lg" onClick={onConfirm} disabled={pending}>
            {pending ? "Suspending..." : "Suspend workspace"}
          </Button>
        </>
      }
    >
      {workspace && (
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-primary)]">
            Suspend <strong>{workspace.name}</strong> ({workspace.productCode} / {workspace.code})?
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            The workspace will be marked suspended and the product will be asked to suspend it. People using this workspace will not be able to access it until you reactivate it.
          </p>
          <p className="text-sm text-[var(--text-secondary)]">
            Its data is retained. You can reactivate the workspace later.
          </p>
        </div>
      )}
    </Sheet>
  );
}

function CreateWorkspaceSheet({
  organizationId,
  open,
  onClose,
}: {
  organizationId: string;
  open: boolean;
  onClose: () => void;
}) {
  const products = useProducts();
  const [productCode, setProductCode] = useState("");
  const plans = usePlans(productCode || undefined);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [planCode, setPlanCode] = useState("");
  const create = useCreateWorkspace(organizationId);

  function submit() {
    create.mutate(
      {
        organizationId,
        productCode,
        code: code.trim().toLowerCase(),
        name: name.trim(),
        planCode: planCode || undefined,
      },
      {
        onSuccess: () => {
          setCode("");
          setName("");
          setPlanCode("");
          onClose();
        },
      },
    );
  }

  const available = (products.data ?? []).filter((product) => product.available);

  return (
    <Sheet
      open={open}
      title="New workspace"
      description="Creation follows billing -- this provisions an instance of a product for the customer."
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="solid"
            size="lg"
            disabled={!productCode || !code.trim() || !name.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? "Provisioning..." : "Provision"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <SelectField
          label="Product"
          required
          options={[
            { value: "", label: "Choose a product" },
            ...available.map((product) => ({ value: product.code, label: product.name })),
          ]}
          value={productCode}
          onChange={(event) => {
            setProductCode(event.target.value);
            setPlanCode("");
          }}
        />
        <SelectField
          label="Plan"
          hint="Sets the starting entitlements. Override them after."
          options={[
            { value: "", label: "No plan yet" },
            ...(plans.data ?? []).map((plan) => ({ value: plan.code, label: plan.name })),
          ]}
          value={planCode}
          onChange={(event) => setPlanCode(event.target.value)}
        />
        <TextField
          label="Code"
          required
          hint="Lowercase, unique per product. The product's own tenant key."
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <TextField
          label="Name"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </div>
    </Sheet>
  );
}

/**
 * An override, not the plan. What the plan grants is seeded at provision time;
 * what is edited here is the effective value a product reads out of its
 * workspace token, so a raised quota takes effect on the next token refresh.
 */
function EntitlementsSheet({
  organizationId,
  workspace,
  onClose,
}: {
  organizationId: string;
  workspace: Workspace | null;
  onClose: () => void;
}) {
  const save = useSetEntitlements(organizationId);
  const moduleAccessEnabled = useModuleFeature();
  const [rows, setRows] = useState<EntitlementRow[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const formKey = workspace ? `${workspace.id}:${moduleAccessEnabled}` : null;

  // Re-seed the form when a different workspace is opened, without an effect:
  // the sheet is only ever rendered for one workspace at a time.
  if (workspace && loadedFor !== formKey) {
    setLoadedFor(formKey);
    setRows(entitlementRows(workspace.entitlements, moduleAccessEnabled));
  }

  function submit() {
    if (!workspace || rows.some((row) => reservedEntitlement(row.key, moduleAccessEnabled))) return;
    const entitlements = entitlementPayload(rows, moduleAccessEnabled);
    save.mutate({ workspaceId: workspace.id, entitlements }, { onSuccess: onClose });
  }

  return (
    <Sheet
      open={workspace !== null}
      title={workspace ? `Entitlements for ${workspace.name}` : ""}
      description={moduleAccessEnabled ? "Manage quotas and other entitlements here. Module grants are managed from Modules." : "Replaces the whole set. A key left blank is dropped."}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="solid" size="lg" disabled={save.isPending || rows.some((row) => reservedEntitlement(row.key, moduleAccessEnabled))} onClick={submit}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {rows.map((row, index) => (
          <div key={index} className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <TextField
                label="Key"
                error={reservedEntitlement(row.key, moduleAccessEnabled) ? "Manage module grants from Modules." : undefined}
                value={row.key}
                onChange={(event) =>
                  setRows(
                    rows.map((item, i) =>
                      i === index ? { ...item, key: event.target.value } : item,
                    ),
                  )
                }
              />
            </div>
            <div className="w-28 shrink-0">
              <TextField
                label="Value"
                value={row.value}
                onChange={(event) =>
                  setRows(
                    rows.map((item, i) =>
                      i === index ? { ...item, value: event.target.value } : item,
                    ),
                  )
                }
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              aria-label="Remove"
              icon={<Trash2 className="size-4" />}
              onClick={() => setRows(rows.filter((_, i) => i !== index))}
            />
          </div>
        ))}

        <Button
          variant="outline"
          className="w-full sm:w-auto"
          icon={<Plus className="size-4" />}
          onClick={() => setRows([...rows, { key: "", value: "" }])}
        >
          Add entitlement
        </Button>

        {rows.length === 0 && (
          <p className="text-sm text-[var(--text-secondary)]">
            {moduleAccessEnabled ? "No additional quotas or entitlements are configured." : "No entitlements. The product falls back to its own defaults."}
          </p>
        )}
      </div>
    </Sheet>
  );
}

function HealthTab({ organizationId }: { organizationId: string }) {
  const health = useHealth(organizationId);
  if (health.isLoading) return <LoadingBlock />;

  return (
    <div className="space-y-3">
      {health.data?.map((entry) => (
        <Card key={entry.workspace.id}>
          <CardHeader
            title={`${entry.workspace.name} (${entry.workspace.productCode})`}
            action={
              <StatusBadge
                status={entry.health.reporting ? "reporting" : "not reporting"}
                tone={entry.health.reporting ? "success" : "neutral"}
              />
            }
          />
          <CardBody>
            {!entry.health.reporting ? (
              <p className="text-sm text-[var(--text-secondary)]">
                {entry.health.message ?? "This product does not expose health yet."}
              </p>
            ) : (
              <ul className="space-y-2">
                {entry.health.readiness.map((item) => (
                  <li key={item.feature} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-[var(--text-primary)]">{item.feature}</div>
                      {item.missing.length > 0 && (
                        <div className="text-xs text-[var(--text-secondary)]">
                          missing: {item.missing.join(", ")}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={item.status} />
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      ))}
      {(health.data?.length ?? 0) === 0 && <EmptyState title="No workspaces to check" />}
    </div>
  );
}

function IncidentsTab({ organizationId }: { organizationId: string }) {
  const incidents = useIncidents(organizationId);
  if (incidents.isLoading) return <LoadingBlock />;

  const any = incidents.data?.some((group) => group.incidents.length > 0);

  if (!any) {
    return (
      <EmptyState
        title="No incidents reported"
        description="Either nothing is failing, or the product does not report incidents yet."
      />
    );
  }

  return (
    <div className="space-y-3">
      {incidents.data?.map((group) =>
        group.incidents.length === 0 ? null : (
          <Card key={group.workspaceId}>
            <CardHeader title={`${group.workspaceName} (${group.productCode})`} />
            <ul className="divide-y divide-[var(--border-subtle)]">
              {group.incidents.map((incident, index) => (
                <li key={`${incident.code}-${index}`} className="px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-[var(--text-primary)]">
                      {incident.code ?? "unknown"}
                    </span>
                    <StatusBadge status={incident.severity ?? "info"} />
                  </div>
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">{incident.message}</p>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-2xs text-[var(--text-muted)]">
                    <span className="numeric">seen {incident.count}x</span>
                    {incident.source && <span>{incident.source}</span>}
                    {incident.requestId && <code>{incident.requestId}</code>}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        ),
      )}
    </div>
  );
}

function NotesTab({ organizationId }: { organizationId: string }) {
  const notes = useNotes(organizationId);
  const add = useAddNote(organizationId);
  const [body, setBody] = useState("");

  return (
    <div className="space-y-3">
      <Card>
        <CardBody className="space-y-3">
          <TextAreaField
            label="Add a note"
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <Button
            variant="solid"
            className="w-full sm:w-auto"
            disabled={!body.trim() || add.isPending}
            onClick={() =>
              add.mutate({ body, pinned: false }, { onSuccess: () => setBody("") })
            }
          >
            {add.isPending ? "Saving..." : "Add note"}
          </Button>
        </CardBody>
      </Card>

      {notes.data?.length === 0 ? (
        <EmptyState title="No notes yet" />
      ) : (
        <Card>
          <ul className="divide-y divide-[var(--border-subtle)]">
            {notes.data?.map((note) => (
              <li key={note.id} className="px-4 py-3">
                <p className="whitespace-pre-wrap text-sm text-[var(--text-primary)]">{note.body}</p>
                <p className="mt-1 text-2xs text-[var(--text-muted)]">
                  {note.author} - {new Date(note.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/**
 * Companies and marketplaces are billable resources, so Nahal Labs creates them and the customer
 * asks through a provisioning request. Only the fields the product cannot default are collected
 * here -- branding, SEO and theme stay the customer's to edit inside the product itself.
 */
function ProvisionSheet({
  organizationId,
  target,
  onClose,
}: {
  organizationId: string;
  target: { workspace: Workspace; kind: "company" | "marketplace" } | null;
  onClose: () => void;
}) {
  const createCompany = useCreateCompany(organizationId);
  const createMarketplace = useCreateMarketplace(organizationId);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");

  const isMarketplace = target?.kind === "marketplace";
  const pending = createCompany.isPending || createMarketplace.isPending;

  function reset() {
    setCode("");
    setName("");
    setDomain("");
    onClose();
  }

  function submit() {
    if (!target) return;
    if (isMarketplace) {
      createMarketplace.mutate(
        {
          workspaceId: target.workspace.id,
          code: code.trim().toUpperCase(),
          name: name.trim(),
          domain: domain.trim() || undefined,
        },
        { onSuccess: reset },
      );
    } else {
      createCompany.mutate(
        { workspaceId: target.workspace.id, code: code.trim().toUpperCase(), name: name.trim() },
        { onSuccess: reset },
      );
    }
  }

  return (
    <Sheet
      key={`${target?.workspace.id ?? "none"}:${target?.kind ?? "none"}`}
      open={target !== null}
      title={isMarketplace ? "New marketplace" : "New company"}
      description={
        target
          ? `Created inside ${target.workspace.productCode} workspace ${target.workspace.code}.`
          : undefined
      }
      onClose={reset}
      footer={
        <>
          <Button variant="outline" size="lg" onClick={reset}>
            Cancel
          </Button>
          <Button
            variant="solid"
            size="lg"
            onClick={submit}
            disabled={pending || !code.trim() || !name.trim()}
          >
            {pending ? "Creating..." : "Create"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <TextField
          label="Code"
          hint={
            isMarketplace
              ? "Uppercase letters, numbers, underscore or hyphen. Cannot be changed later."
              : "Short code, up to 10 characters. Cannot be changed later."
          }
          value={code}
          onChange={(event) => setCode(event.target.value)}
          required
        />
        <TextField
          label="Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
        />
        {isMarketplace && (
          <TextField
            label="Domain"
            hint="The storefront host. DNS and the certificate are a separate step."
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
          />
        )}
      </div>
    </Sheet>
  );
}
